import React, { useEffect } from 'react';
import { DropdownMenu, TruncatedText } from '@spark';
import { useWalkthroughAutoOpen } from '@/features/walkthrough';
import { HelpIcon } from '../shared/TaskTypeControls';
import { buildLabelPriorityOptions, labelPriorityValue } from './labelPriority';

/**
 * "What matters most for these labels?" single-select for single-label
 * classification tasks with exactly two labels (Step 1, Business Goals).
 * Replaces the Fallback label select for options 2–5 (the fallback is then
 * the other label) and sits next to it for "Both labels are equally
 * important". Options follow label edits live; the field is required
 * whenever it is shown.
 *
 * The options carry the label names, so the panel grows to fit the longest
 * one (`cw-label-options-panel`, shared with FallbackLabelSelect) instead of
 * ellipsizing at the trigger width; an option only gets a tooltip when the
 * viewport cap still clips it. `searchText` keeps the plain label available
 * to DropdownMenu, whose item keys and keyboard highlight read it when the
 * label is a node. The walkthrough opens the menu when its step is reached;
 * `autoOpen` opens it on arrival from Data Collection's "Adjust Label
 * Priorities".
 */
const LabelPrioritySelect = ({ labels = [], value, onChange, error, autoOpen = false }) => {
    const [open, setOpen] = useWalkthroughAutoOpen('label-priority');
    useEffect(() => {
        if (autoOpen) setOpen(true);
    }, [autoOpen, setOpen]);
    const options = buildLabelPriorityOptions(labels);
    const selectedValue = labelPriorityValue(value);
    const selected = options.find((o) => o.value === selectedValue);

    return (
        <div className="cw-fallback-label-section" data-walkthrough="label-priority">
            <div className="cw-section-label">
                What matters most for these labels?
                <span className="cw-required-asterisk">*</span>
                <HelpIcon
                    content={
                        <>
                            Sets the <strong>Fallback</strong> label and the training defaults
                            (F score beta and the label to optimize on). You can still adjust
                            them in the Train step&apos;s Advanced section.
                        </>
                    }
                />
            </div>
            <div className="cw-fallback-label-section__select">
                <DropdownMenu
                    selectType="single-select"
                    showLabel={false}
                    size="medium"
                    placeholder="Select what matters most"
                    text={selected?.label}
                    panelLayout="overlay"
                    panelClassName="cw-label-options-panel"
                    open={open}
                    onOpenChange={setOpen}
                    items={options.map((o) => ({
                        label: <TruncatedText text={o.label} maxWidth="100%" />,
                        searchText: o.label,
                        state: o.value === selectedValue ? 'selected' : 'default',
                        onClick: () => {
                            onChange(o.choice);
                            setOpen(false);
                        },
                    }))}
                />
            </div>
            {error && <div className="cw-field-error">{error}</div>}
        </div>
    );
};

export default LabelPrioritySelect;
